import { DEFAULT_ASSET_TYPE_TITLE, type LocationData } from '@ikegps/core-api';
import type { FormModelFieldSubForm } from '../form/subform/form.subform';
import type { CollectionModel } from './collection.data';
import { CollectionModelField } from './collection.field';
import { type CollectionAssetTypeValue, CollectionModelFieldAssetType } from './fields/asset.type.field';
import { createField } from './fields/field.registry';
import { CollectionModelFieldSubForm } from './fields/form.field';
import { CollectionModelFieldLine } from './fields/line.field';
import { CollectionModelFieldLocation } from './fields/location.field';
import { CollectionModelFieldTrueSize } from './fields/true.size.field';

export abstract class CollectionModelBase {
	parent: CollectionModelFieldSubForm;
	schema: FormModelFieldSubForm;
	private _instanceId: string;
	public get instanceId(): string {
		return this._instanceId;
	}
	public set instanceId(value: string) {
		this._instanceId = value;
	}
	private fields: { [fieldId: string]: CollectionModelField } = {};
	abstract get collection(): CollectionModel;

	constructor(parent: CollectionModelFieldSubForm, id: string, schema: FormModelFieldSubForm) {
		this.parent = parent;
		this.schema = schema;
		if (id != null) {
			this.instanceId = id;
		}
	}

	getField<T extends CollectionModelField>(fieldId: string): T {
		let field = this.fields[fieldId];
		if (field == null) {
			const fieldSchema = this.schema.getField(fieldId);
			if (fieldSchema == null) {
				throw new Error(`Unable to find field ${this.instanceId}.${fieldId}`);
			}
			field = createField(this, fieldSchema);
			this.fields[fieldId] = field;
		}

		return field as T;
	}

	getOptionalField<T extends CollectionModelField>(fieldId: string): T | null {
		let field = this.fields[fieldId];
		if (field == null) {
			if (!this.schema.hasField(fieldId)) {
				return null;
			}
			const fieldSchema = this.schema.getField(fieldId);
			field = createField(this, fieldSchema);
			this.fields[fieldId] = field;
		}

		return field as T;
	}

	getFields(): CollectionModelField[] {
		return this.schema.fields.map((field) => this.getField(field.id));
	}

	getFieldDeep<T extends CollectionModelField>(fieldId: string): T | null {
		let result: T | null = null;

		this.forEachField((x) => {
			if (fieldId === x.id) {
				result = x as T;
			}
		});

		return result;
	}

	hasField(fieldId: string): boolean {
		return (
			this.fields[fieldId] != null ||
			this.collection.state.hasFieldValue(CollectionModelField.getStateId(this, fieldId))
		);
	}

	get title(): string | null {
		const titleField = this.schema.titleField;
		if (titleField == null) {
			return this.formTitle;
		}

		const titleCollectionField = this.getField(titleField.id);
		if (
			!titleCollectionField.hasValue() ||
			titleCollectionField.displayValue == null ||
			titleCollectionField.displayValue === ''
		) {
			return this.formTitle;
		}

		return titleCollectionField.displayValue;
	}

	get titleField(): string {
		const titleField = this.schema.titleField;
		return titleField.id;
	}

	get subtitle(): string | null {
		const subtitleField = this.schema.subtitleField;
		if (subtitleField == null) {
			return null;
		}

		const subtitleCollectionField = this.getField(subtitleField.id);
		if (
			!subtitleCollectionField.hasValue() ||
			subtitleCollectionField.displayValue == null ||
			subtitleCollectionField.displayValue === ''
		) {
			return null;
		}

		return subtitleCollectionField.displayValue;
	}

	get siteType(): string | null {
		const siteTypeField = this.schema.siteTypeField;
		if (siteTypeField == null) {
			return null;
		}

		const siteTypeCollectionField = this.getField(siteTypeField.id);
		if (
			!siteTypeCollectionField.hasValue() ||
			siteTypeCollectionField.displayValue == null ||
			siteTypeCollectionField.displayValue === ''
		) {
			return null;
		}

		return siteTypeCollectionField.value;
	}

	get dropPole(): string | null {
		const dropPoleField = this.schema.dropPoleField;
		if (dropPoleField == null) {
			return null;
		}

		const dropPoleCollectionField = this.getField(dropPoleField.id);
		if (
			!dropPoleCollectionField.hasValue() ||
			dropPoleCollectionField.displayValue == null ||
			dropPoleCollectionField.displayValue === ''
		) {
			return null;
		}

		return dropPoleCollectionField.value;
	}

	get poleLoading(): string | null {
		const poleLoadingField = this.schema.poleLoadingField;
		if (poleLoadingField == null) {
			return null;
		}

		const poleLoadingCollectionField = this.getField(poleLoadingField.id);
		if (
			!poleLoadingCollectionField.hasValue() ||
			poleLoadingCollectionField.displayValue == null ||
			poleLoadingCollectionField.displayValue === ''
		) {
			return null;
		}

		return poleLoadingCollectionField.value;
	}

	/** The first `assettype` field in this collection (including nested sub-forms), if any. */
	get assetTypeField(): CollectionModelFieldAssetType | null {
		let result: CollectionModelFieldAssetType | null = null;
		this.forEachField((field) => {
			if (CollectionModelFieldAssetType.isAssetTypeField(field)) {
				result = field;
				return false;
			}
			return true;
		});
		return result;
	}

	/**
	 * The collection's selected asset type — icon, color, title — resolved from its `assettype`
	 * field. Null if there's no such field, no value is selected, the selected title no longer
	 * resolves against the schema (e.g. a since-removed custom type), or the selection is the
	 * "no particular asset type" default (@see DEFAULT_ASSET_TYPE_TITLE) — map markers, the legend,
	 * KML, and shapefile all render that case as a plain dot, same as no asset type at all.
	 */
	get assetType(): CollectionAssetTypeValue | null {
		const field = this.assetTypeField;
		if (field == null || !field.hasValue()) {
			return null;
		}

		const { icon, color, value: title } = field;
		if (icon == null || color == null || title == null || title === DEFAULT_ASSET_TYPE_TITLE) {
			return null;
		}

		return { icon, color, title };
	}

	/**
	 * If this collection has an `assettype` field with no value selected, defaults it to the seeded
	 * "Collection" preset. Used when a form migration/upgrade adds the field to existing data, or an
	 * uploaded collection omits it outright — both cases would otherwise leave the field unset.
	 */
	applyDefaultAssetType(): void {
		const field = this.assetTypeField;
		if (field != null && !field.hasValue()) {
			field.Mutator.manual(DEFAULT_ASSET_TYPE_TITLE);
		}
	}

	/**
	 * Returns true if the collection has a true-size field but none of those fields have captures.
	 */
	isMissingTrueSize(): boolean {
		let hasTrueSizeField = false;
		for (const field of this.getFields()) {
			if (CollectionModelFieldTrueSize.isMeasurableIkePhotoField(field)) {
				hasTrueSizeField = true;
				if (field.getCaptureIds().length > 0) {
					return false;
				}
			}
		}
		return hasTrueSizeField;
	}

	/**
	 * Return the first location found in the collection.
	 * This is either the first location field or, if none exists, the first IKE photo capture’s location.
	 * A MidSpan field contributes its span capture only — never the ground reference shot.
	 */
	get location(): LocationData {
		const result = {
			locationValue: <LocationData>null,
			trueSizeValue: <LocationData>null,
		};

		this.forEachField((field) => {
			if (CollectionModelFieldLocation.isLocationField(field) && field.hasValue()) {
				result.locationValue = field.value;
				return false; // break the loop early
			}

			if (result.trueSizeValue == null && CollectionModelFieldTrueSize.isMeasurableIkePhotoField(field)) {
				const captures = field.getOwnedCaptures();
				if (captures.length > 0) {
					result.trueSizeValue = captures[0].target;
				}
			}
			return true;
		});

		return result.locationValue || result.trueSizeValue;
	}

	/**
	 * Returns the location field found in the collection
	 * This is either in the first location field or, if none exists, the first truesize image is used.
	 */
	get locationModelField(): CollectionModelFieldLocation | CollectionModelFieldTrueSize {
		let result = {
			location: <CollectionModelFieldLocation>null,
			truesize: <CollectionModelFieldTrueSize>null,
		};

		this.forEachField((field) => {
			if (CollectionModelFieldLocation.isLocationField(field) && field.hasValue()) {
				result.location = field;
				return false;
			}

			return true;
		});

		if (!result.location) {
			this.forEachField((field) => {
				if (!result.truesize && CollectionModelFieldTrueSize.isMeasurableIkePhotoField(field)) {
					result.truesize = field;
					return false;
				}

				return true;
			});
		}

		return result.location || result.truesize;
	}

	/**
	 * Get the first location field (not value) in the form (not truesize)
	 */
	get editableLocationField(): CollectionModelFieldLocation {
		let locationField: CollectionModelFieldLocation | null = null;

		this.forEachField((field) => {
			if (CollectionModelFieldLocation.isLocationField(field)) {
				locationField = field;
				return false; // break loop after location is found
			}
			return true;
		});
		return locationField;
	}

	/**
	 * Iterate over all fields in the collection,
	 * This will do a depth first traversal.
	 *
	 * Returning false at any stage will abort the traversal.
	 *
	 * @param func Function to call on every field.
	 * @returns Whether all fields were processed
	 */
	forEachField(func: (collectionField: CollectionModelField) => boolean | any): boolean {
		for (const schema of this.schema.fields) {
			const collectionField = this.getField(schema.id);
			const result = func(collectionField);
			if (result === false) {
				return false;
			}

			if (CollectionModelFieldSubForm.isSubFormField(collectionField)) {
				for (const instance of collectionField.value) {
					const res = instance.forEachField(func);
					if (res === false) {
						return false;
					}
				}
			}
		}
		return true;
	}

	/** Every saved (>=2-point) line field in this collection, including those nested in sub-forms. */
	savedLineFields(): CollectionModelFieldLine[] {
		const lines: CollectionModelFieldLine[] = [];
		this.forEachField((field) => {
			if (CollectionModelFieldLine.isLineField(field) && field.hasValue()) {
				lines.push(field);
			}
		});
		return lines;
	}

	get formTitle(): string | null {
		return null;
	}
}

export class CollectionModelFieldSubFormInstance extends CollectionModelBase {
	get collection(): CollectionModel {
		return this.parent.collection;
	}

	override get formTitle(): string {
		const name = this.schema.displayName || '';

		const parent = this.parent;
		const index = parent.value.indexOf(this);

		return `${name} #${index + 1}`;
	}
}
